import { useState } from 'react';
import { useApiMutation, useVideos } from '../api/hooks';
import { DataTable } from '../components/DataTable';
import { Card, ConfirmButton, ErrorMessage, Field, formatDate, PageHeader } from '../components/ui';

export function VideosPage() {
  const videos = useVideos();
  const [ytTitle, setYtTitle] = useState('');
  const [ytUrl, setYtUrl] = useState('');
  const keys = [['admin', 'videos']];
  const addYouTube = useApiMutation<void>(
    () => ({ path: '/admin/videos/youtube', method: 'POST', body: { title: ytTitle, url: ytUrl } }),
    keys,
  );
  const remove = useApiMutation<string>(
    (id) => ({ path: `/admin/videos/${id}`, method: 'DELETE' }),
    [...keys, ['admin', 'techniques']],
  );

  return (
    <>
      <PageHeader
        title="Videos"
        subtitle="Videos are attached to techniques in the curriculum editor. Changing a video never affects student progress."
      />
      <Card title="Add a YouTube video">
        <div className="alert warning">
          Unlisted YouTube links are not private: anyone with the link can watch, and students who
          saved a link keep access after their account is disabled.
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            addYouTube.mutate(undefined, {
              onSuccess: () => {
                setYtTitle('');
                setYtUrl('');
              },
            });
          }}
        >
          <ErrorMessage error={addYouTube.error} />
          <Field label="Title">
            {(id) => (
              <input
                id={id}
                type="text"
                required
                maxLength={160}
                value={ytTitle}
                onChange={(e) => setYtTitle(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="YouTube link"
            hint="Upload to YouTube as Unlisted (not Private) with embedding allowed, then paste the link."
          >
            {(id) => (
              <input
                id={id}
                type="url"
                required
                value={ytUrl}
                onChange={(e) => setYtUrl(e.target.value)}
                placeholder="https://youtu.be/…"
              />
            )}
          </Field>
          <button className="btn primary" type="submit" disabled={addYouTube.isPending}>
            Add video
          </button>
        </form>
      </Card>
      <Card title="Library">
        <ErrorMessage error={videos.error ?? remove.error} />
        <DataTable
          rows={videos.data?.items}
          rowKey={(v) => v.id}
          empty="No videos yet."
          columns={[
            {
              key: 'thumb',
              header: '',
              width: 96,
              render: (v) => (
                <img src={v.thumbnailUrl} alt="" width={88} style={{ borderRadius: 4 }} />
              ),
            },
            {
              key: 'title',
              header: 'Title',
              render: (v) => (
                <a
                  href={`https://www.youtube.com/watch?v=${v.youtubeVideoId}`}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {v.title}
                </a>
              ),
            },
            {
              key: 'used',
              header: 'Used by',
              render: (v) =>
                v.usedBy.length ? (
                  v.usedBy.map((t) => t.name).join(', ')
                ) : (
                  <span className="muted">Unused</span>
                ),
            },
            { key: 'created', header: 'Added', render: (v) => formatDate(v.createdAt) },
            {
              key: 'actions',
              header: '',
              render: (v) => (
                <ConfirmButton
                  confirmLabel={v.usedBy.length ? 'Detach & delete?' : 'Delete?'}
                  onConfirm={() => remove.mutate(v.id)}
                >
                  Delete
                </ConfirmButton>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
